import type { SdkConfig } from '@ekhodealer/ekho-common';

const CONFIG_TIMEOUT_MS = 8000;
const RETRY_DELAY_MS = 1000;
const SDK_VERSION = '1';

export interface FetchConfigOptions {
  endpoint: string;
  key: string;
}

/**
 * Distinguishes a definitive rejection (the server answered with 4xx — invalid
 * or unknown key) from a transient failure (network error, timeout). The loader
 * rejects `Ekho.ready` on the former and silently deactivates on the latter.
 */
export class ConfigFetchError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'ConfigFetchError';
    this.status = status;
  }

  get isClientError(): boolean {
    return this.status !== null && this.status >= 400 && this.status < 500;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchOnce(url: string): Promise<SdkConfig> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { 'X-Ekho-SDK-Version': SDK_VERSION },
      signal: controller.signal,
      credentials: 'omit',
    });
    if (!res.ok) {
      throw new ConfigFetchError(`config fetch failed: ${res.status}`, res.status);
    }
    return (await res.json()) as SdkConfig;
  } finally {
    clearTimeout(timer);
  }
}

function buildUrl(options: FetchConfigOptions): string {
  const separator = options.endpoint.includes('?') ? '&' : '?';
  return `${options.endpoint}${separator}key=${encodeURIComponent(options.key)}`;
}

/**
 * Fetch SDK config with an 8s hard timeout and one retry on transient failure.
 * A 4xx response is not retried — it rethrows immediately so the caller can
 * reject `Ekho.ready` for an invalid or unknown key.
 */
export async function fetchConfig(options: FetchConfigOptions): Promise<SdkConfig> {
  const url = buildUrl(options);
  try {
    return await fetchOnce(url);
  } catch (err) {
    if (err instanceof ConfigFetchError && err.isClientError) throw err;
    await delay(RETRY_DELAY_MS);
    return fetchOnce(url);
  }
}
